import { useDashboardStore } from '@/state/store';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { ActorIcon } from '@/components/ActorIcon';
import type { AnyEnvelope, EventActor, EventMood } from '@/events';

const MOOD_COLOR: Record<EventMood | string, string> = {
  idle: '#8b949e',
  thinking: '#58a6ff',
  confident: '#2ea043',
  worried: '#d29922',
  excited: '#db61a2',
  angry: '#f85149',
  proud: '#a371f7',
  sad: '#6e7681',
  frozen: '#58a6ff',
  celebrating: '#ffd33d',
};

function relTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return iso;
  if (ms < 1_000) return 'now';
  if (ms < 60_000) return `${Math.floor(ms / 1_000)}s`;
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m`;
  return `${Math.floor(ms / 3_600_000)}h`;
}

function summary(env: AnyEnvelope): string {
  if (env.narrative) return env.narrative;
  const d = env.data;
  if (env.type === 'agent.signal_emitted') {
    const top = (d.top_tickers as string[] | undefined)?.slice(0, 3).join(', ');
    return top ? `top: ${top}` : 'signal emitted';
  }
  if (env.type === 'pm.decision_made') {
    return String(d.ui_summary ?? d.rationale ?? 'PM decision');
  }
  if (env.type === 'risk.state_changed') {
    return `${d.from} → ${d.to} (${d.reason ?? ''})`;
  }
  if (env.type === 'order.filled') return `${d.ticker} ${d.side} @ ${d.price}`;
  if (env.type === 'order.submitted') return `${d.ticker} ${d.side}`;
  if (env.type === 'utility.recorded') return `total ${Number(d.total).toFixed(3)}`;
  if (env.type === 'building.weather_changed') return `weather → ${d.weather_kind}`;
  if (env.type === 'building.sun_position') return `phase ${d.phase}`;
  if (env.type === 'news.event_ingested') {
    // Headline is the only thing operators care about; per-ticker
    // sentiment renders as separate chips (see Row()).
    return String(d.title ?? 'news event');
  }
  if (env.type === 'news.ticker_sentiment_shift') {
    const ticker = String(d.ticker ?? '?');
    const cur = Number(d.current_sentiment).toFixed(2);
    const base = Number(d.baseline_sentiment).toFixed(2);
    const trigger = d.trigger ? ` (${d.trigger})` : '';
    return `${ticker}: ${base} → ${cur}${trigger}`;
  }
  if (env.type === 'news.digest_published') {
    return `digest · ${d.total_events ?? 0} events`;
  }
  return env.type;
}

// Sentiment → CSS colour. Centralised so per-ticker chips and the
// digest line use the same palette.  Returns rgb-strings rather than
// hex so we can interpolate the negative/positive halves.
function sentimentColor(value: number): string {
  if (Number.isNaN(value)) return '#8b949e';
  if (value > 0.05) return '#2ea043'; // positive — green
  if (value < -0.05) return '#f85149'; // negative — red
  return '#8b949e'; // neutral — grey
}

function NewsTickerChips({ env }: { env: AnyEnvelope }) {
  // Only ``news.event_ingested`` carries per-ticker sentiment; the
  // other news types render through ``summary()`` only.
  if (env.type !== 'news.event_ingested') return null;
  const byTicker = (env.data?.sentiment_by_ticker ?? {}) as Record<string, number>;
  const tickers = Object.keys(byTicker);
  if (tickers.length === 0) return null;
  // Limit to 4 chips so the row stays single-line even at narrow widths;
  // operators can hover the headline summary for the full list.
  const shown = tickers.slice(0, 4);
  return (
    <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
      {shown.map((ticker) => {
        const s = Number(byTicker[ticker]);
        const color = sentimentColor(s);
        return (
          <span
            key={ticker}
            title={`${ticker} sentiment for this article: ${s.toFixed(2)}`}
            style={{
              fontSize: 10,
              padding: '1px 6px',
              borderRadius: 8,
              background: '#161b22',
              color,
              border: `1px solid ${color}`,
              fontFamily: 'monospace',
            }}
          >
            {ticker} {s >= 0 ? '+' : ''}
            {s.toFixed(2)}
          </span>
        );
      })}
      {tickers.length > shown.length ? (
        <span style={{ fontSize: 10, color: '#6e7681' }}>+{tickers.length - shown.length}</span>
      ) : null}
    </span>
  );
}

export function EventFeed() {
  const feed = useDashboardStore((s) => s.feed);
  const bp = useBreakpoint();
  const reversed = [...feed].reverse();
  const isMobile = bp === 'mobile';
  return (
    <aside
      style={{
        borderLeft: isMobile ? 'none' : '1px solid #21262d',
        borderTop: isMobile ? '1px solid #21262d' : 'none',
        background: '#0d1117',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      <header
        style={{
          padding: isMobile ? '6px 12px' : '10px 14px',
          borderBottom: '1px solid #21262d',
          fontSize: isMobile ? 10 : 12,
          color: '#8b949e',
          textTransform: 'uppercase',
          letterSpacing: 1,
        }}
        title={`Хронологический поток всех событий через WebSocket: cycle-события, ордера, риск-вердикты, policy-updates, ambient (idle_action). Свежие сверху, окно ограничено ${feed.length} строк. Backend хранит больше в Redis events:all — если нужен глубже backlog, переподключайся с since_id.`}
      >
        Event feed · {feed.length}
      </header>
      <div style={{ overflowY: 'auto', flex: 1 }}>
        {reversed.length === 0 ? (
          <div style={{ padding: 16, color: '#6e7681', fontSize: 12 }}>
            Awaiting first event…
          </div>
        ) : (
          reversed.map((env) => <Row key={env.id} env={env} />)
        )}
      </div>
    </aside>
  );
}

function Row({ env }: { env: AnyEnvelope }) {
  const selectPod = useDashboardStore((s) => s.selectPod);
  const podName = useDashboardStore((s) => {
    if (!env.subject.startsWith('pod:')) return null;
    const podId = env.subject.slice('pod:'.length);
    const summary = s.building?.floors
      .flatMap((f) => f.pods)
      .find((p) => p.pod_id === podId);
    return summary?.name ?? null;
  });
  const actor = env.scene_hint?.actor ?? 'system';
  const mood = env.scene_hint?.mood;
  const accent = mood ? MOOD_COLOR[mood] ?? '#8b949e' : '#8b949e';
  const actorLabel = ACTOR_LABEL[actor] ?? actor;
  const podId = env.subject.startsWith('pod:')
    ? env.subject.slice('pod:'.length)
    : null;
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '28px 1fr auto',
        padding: '6px 10px',
        borderBottom: '1px solid #161b22',
        fontSize: 11,
        gap: 8,
        alignItems: 'center',
      }}
    >
      <span
        title={`Актор события: ${actorLabel}. Цвет границы кодирует mood из scene_hint — это эмоциональная подсказка для аниматора: confident (зелёный) / worried (оранжевый) / angry (красный) / idle (серый).`}
        aria-label={actorLabel}
        style={{
          width: 24,
          height: 24,
          borderRadius: '50%',
          background: '#f2e8c9',
          border: `2px solid ${accent}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <ActorIcon kind={actor} size={13} title={actorLabel} />
      </span>
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            color: '#e6edf3',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            flexWrap: 'wrap',
          }}
        >
          <span title={`Тип события: ${env.type}. Полная спецификация — в docs/dashboard_events.schema.json. Префикс показывает scope: agent.* / pod.* / building.* / cycle.* / order.* / risk.* / policy.* / centerbook.*`}>
            {env.type}
          </span>
          {mood && (
            <span
              style={{ color: accent, fontStyle: 'italic' }}
              title={`Mood из scene_hint: ${mood}. Подсказка для аниматора — какую эмоцию рисовать у персонажа в этот момент.`}
            >
              {mood}
            </span>
          )}
          {podId ? (
            <button
              type="button"
              onClick={() => selectPod(podId)}
              title={`Открыть детали пода: ${podName ?? podId}. Откроется pod_room — комната с агентами, текущим портфелем и таймлайном.`}
              style={{
                fontSize: 10,
                padding: '1px 6px',
                borderRadius: 8,
                background: '#161b22',
                color: '#8b949e',
                border: '1px solid #21262d',
                cursor: 'pointer',
                fontFamily: 'inherit',
                whiteSpace: 'nowrap',
                maxWidth: 140,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {podName ?? podId.replace(/^pod_moex_/, '')}
            </button>
          ) : null}
          <NewsTickerChips env={env} />
        </div>
        <div
          style={{
            color: '#8b949e',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          title={summary(env)}
        >
          {summary(env)}
        </div>
      </div>
      <span
        style={{ color: '#6e7681' }}
        title={`Точное время события (UTC): ${env.ts}.\n\nID для backlog-replay: ${env.id}.`}
      >
        {relTime(env.ts)}
      </span>
    </div>
  );
}

const ACTOR_LABEL: Record<EventActor | string, string> = {
  qa: 'Quant',
  sa: 'Sentiment',
  fa: 'Fundamental',
  researcher: 'Researcher',
  rm: 'Risk Manager',
  ea: 'Execution',
  pm: 'Portfolio Manager',
  risk_officer: 'Risk Officer',
  center_book: 'Center Book',
  discipline_gate: 'Discipline Gate',
  policy_updater: 'Policy Updater',
  leaderboard_keeper: 'Leaderboard Keeper',
  system: 'System',
  trader: 'Trader',
};
